import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { Media } from '@nucleus-commerce/api-client/admin';

import { MediaIdField, MediaUploadButton, useMedia, useMediaUploader } from '@/features/media';

import { isReviewMediaType, isTooLarge, REVIEW_MEDIA_TYPES } from '../schemas/file-limits';

export interface QcFileAnswerProps {
  label: string;
  value: string | null;
  onChange: (mediaId: string | null) => void;
  /** Reports whether the chosen file may be submitted (image or video, 20 MB at most). */
  onValidity: (problem: string | null) => void;
}

/**
 * A QC file answer: a photo or video picked from the media library or uploaded on the spot.
 * Both paths are checked against the backend's 20 MB limit before anything is submitted.
 */
export function QcFileAnswer({ label, value, onChange, onValidity }: QcFileAnswerProps) {
  const { t } = useTranslation('production');
  const [problem, setProblem] = useState<string | null>(null);
  const { data: chosen } = useMedia(value);

  const check = (media: Pick<Media, 'mimeType' | 'bytes'>, name: string) => {
    if (!isReviewMediaType(media.mimeType)) return t('media.wrongType', { name });
    if (isTooLarge(media.bytes)) return t('media.tooLarge', { name });
    return null;
  };

  const latest = useRef(onChange);
  useLayoutEffect(() => {
    latest.current = onChange;
  });
  const onUploaded = useCallback((media: Media) => latest.current(media.id), []);
  const uploader = useMediaUploader({ onUploaded });
  const busy = uploader.items.some(
    (item) => item.status === 'uploading' || item.status === 'queued',
  );
  const chosenProblem = chosen ? check(chosen, chosen.alt ?? t('media.untitled')) : null;
  const shown = problem ?? chosenProblem;
  useEffect(() => {
    onValidity(shown);
  }, [onValidity, shown]);

  return (
    <div className="flex flex-col gap-2">
      <MediaIdField
        label={label}
        value={value}
        onChange={(mediaId) => {
          setProblem(null);
          onChange(mediaId);
        }}
      />
      <div className="flex items-center gap-2">
        <MediaUploadButton
          variant="outline"
          size="sm"
          multiple={false}
          accept={REVIEW_MEDIA_TYPES}
          aria-label={t('media.uploadLabel', { name: label })}
          disabled={busy}
          onFiles={(files) => {
            const file = files[0];
            if (!file) return;
            const refused = check({ mimeType: file.type, bytes: file.size }, file.name);
            setProblem(refused);
            if (!refused) uploader.add([file]);
          }}
        >
          {busy ? t('media.uploadingShort') : t('media.upload')}
        </MediaUploadButton>
      </div>
      {shown ? (
        <p role="alert" className="text-destructive text-sm">
          {shown}
        </p>
      ) : null}
    </div>
  );
}
